import { useQuery } from '@tanstack/react-query';
import { api, type Recovery } from '../lib/api';
import { when } from '../lib/format';

/**
 * D11 escape hatch, requester side. Shown on /login after an email code for an account that has
 * passkeys: no session was opened; the recovery completes after the delay unless the passkey holder
 * cancels. Polls the public status so the page follows a cancel or completion.
 */
export function RecoveryPending({
  recovery,
  email,
  onReset,
}: {
  recovery: Recovery;
  email: string;
  onReset: () => void;
}) {
  const live = useQuery({
    queryKey: ['recovery', recovery.id],
    queryFn: () => api.recovery(recovery.id),
    refetchInterval: (q) => (q.state.data?.recovery.status === 'pending' ? 15_000 : false),
    initialData: { recovery },
  });
  const r = live.data?.recovery ?? recovery;
  return (
    <div className="stack">
      <h2 style={{ marginTop: 0 }}>Account recovery</h2>
      {r.status === 'pending' && (
        <>
          <p>
            <b>{email}</b> is protected by a passkey, so an email code does not sign you in. Your
            code started an <b>account recovery</b> instead. It completes{' '}
            <b>{when(r.completesAt)}</b>.
          </p>
          <p className="muted">
            We emailed <b>{email}</b> about this with a cancel link. If you still have a passkey for
            this account, sign in with it and cancel from Account. When the recovery completes,
            every session is signed out and all passkeys are removed; you then sign in with an email
            code and add a new passkey.
          </p>
          <p className="muted">
            Recovery id <code>{r.id}</code>. You can close this page; come back after{' '}
            {when(r.completesAt)} and request a new code.
          </p>
        </>
      )}
      {r.status === 'cancelled' && (
        <p className="warn">This recovery was cancelled {when(r.cancelledAt ?? r.requestedAt)}.</p>
      )}
      {r.status === 'completed' && (
        <p className="ok">
          Recovery complete. Request a new code to sign in, then add a new passkey.
        </p>
      )}
      <button type="button" onClick={onReset}>
        Back to sign in
      </button>
    </div>
  );
}

/** Public /recovery/cancel?token=… from the alert email. Single use. */
export function RecoveryCancelPage({ token }: { token: string | undefined }) {
  const q = useQuery({
    queryKey: ['recovery-cancel', token],
    queryFn: () => api.cancelRecoveryWithToken(token as string),
    enabled: Boolean(token),
    retry: false,
  });
  return (
    <div className="center">
      <div className="panel card stack">
        <h1>Cancel account recovery</h1>
        {!token && <p className="error">This link is missing its token.</p>}
        {q.isPending && token && <p className="muted">Cancelling…</p>}
        {q.error && (
          <p className="error">
            This link is not valid any more. It may have been used already, or the recovery already
            finished. If you did not expect a recovery, sign in with your passkey and check Account.
          </p>
        )}
        {q.data && q.data.recovery.status === 'cancelled' && (
          <p className="ok">
            Recovery cancelled {when(q.data.recovery.cancelledAt ?? q.data.recovery.requestedAt)}.
            Your passkeys and sessions are unchanged.
          </p>
        )}
        {q.data && q.data.recovery.status === 'completed' && (
          <p className="warn">
            Too late: this recovery already completed {when(q.data.recovery.completedAt ?? '')}.
            Sign in with an email code and add a new passkey; if that was not you, contact support.
          </p>
        )}
        <a href="/login">Go to sign in</a>
      </div>
    </div>
  );
}

/** Account-page banner for the passkey holder: loud, with the cancel button. */
export function RecoveryBanner({
  recovery,
  canCancel,
  onCancel,
  busy,
}: {
  recovery: Recovery;
  canCancel: boolean;
  onCancel: () => void;
  busy: boolean;
}) {
  return (
    <section className="panel stack" style={{ borderColor: 'var(--danger, #c33)' }}>
      <h2 style={{ marginTop: 0 }}>Account recovery in progress</h2>
      <p>
        Someone with access to your email requested a recovery {when(recovery.requestedAt)}. Unless
        cancelled, <b>{when(recovery.completesAt)}</b> every session is signed out and all passkeys
        are removed, and email alone signs in.
      </p>
      {canCancel ? (
        <div className="row">
          <button type="button" className="primary" onClick={onCancel} disabled={busy}>
            Cancel this recovery
          </button>
          <span className="muted">If this was you and you lost your other passkeys, leave it.</span>
        </div>
      ) : (
        <p className="muted">
          Sign in with a passkey to cancel it, or use the link in the alert email.
        </p>
      )}
    </section>
  );
}
